/**
 * A SECRET CONNECTION (PLUGINS.md §3, contract v1 ``auth: secret``) — connect an outside system that hands out a key /
 * token (no federated identity) for any connection type a PLUGIN contributes, as a 2-step stepper:
 *   1. Details — name the connection + the plugin's config fields + its SECRET fields (password inputs). Secrets go to
 *                the platform's secrets store (``plaintextSecrets``), never into ``config``; editing leaves a blank
 *                secret unchanged.
 *   2. Test    — the platform resolves the stored values, the plugin checks them → TESTED_OK.
 * Workload tier only — the organization (admin) connection needs a federated identity. The vendor's words and fields
 * come from the plugin's ``wizard`` + ``secretFields`` — this component names no vendor.
 */
import React, { useMemo, useState } from 'react';
import { Loader2, KeyRound, CheckCircle2, XCircle } from 'lucide-react';
import {
  createSecretsRegistrySecret, updateSecretsRegistrySecret, testSecretsRegistrySecret,
} from '../../services/toolsApi';
import { fieldOk } from './FederatedConnectionWizard.jsx';

const STEPS = ['Details', 'Test'];

/** PURE — the save body for a secret connection (blank secrets are left out: on create they fail the required check
 *  server-side, on edit they keep the stored value). */
export function secretSaveBody({ fields, secretFields, values, secrets, editing }) {
  const config = { ...Object.fromEntries((fields || []).map((f) => [f.id, String(values?.[f.id] || '').trim()])),
    TIER: 'workload' };
  const plaintextSecrets = {};
  for (const f of (secretFields || [])) {
    const v = String(secrets?.[f.id] || '');
    if (v) plaintextSecrets[f.id] = v;
  }
  return editing ? { config, ...(Object.keys(plaintextSecrets).length ? { plaintextSecrets } : {}) }
    : { config, plaintextSecrets };
}

export default function SecretConnectionWizard({ open, spec, initialEntry, registryScope, onRegistryCreated, onCancel }) {
  const w = spec?.wizard || {};
  const fields = useMemo(() => w.fields || [], [w.fields]);
  const secretFields = useMemo(() => (spec?.secretFields || []).filter((f) => f && f.id), [spec?.secretFields]);
  const editing = !!initialEntry?.secretId;
  const [step, setStep] = useState(0);
  const [label, setLabel] = useState(initialEntry?.instanceLabel || '');
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.id,
    initialEntry?.values?.[f.id] || f.default || ''])));
  const [secrets, setSecrets] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(null);
  if (!open || !spec) return null;
  const secretsOk = editing || secretFields.every((f) => f.required === false || String(secrets[f.id] || ''));
  const allOk = fields.every((f) => fieldOk(f, values[f.id])) && secretsOk;
  const saveAndTest = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      let row = saved;
      if (!row) {
        const body = secretSaveBody({ fields, secretFields, values, secrets, editing });
        if (editing) {
          const d = await updateSecretsRegistrySecret(initialEntry.secretId, { secretName: label.trim(), ...body },
            typeof initialEntry.version === 'number' ? { ifMatch: String(initialEntry.version) } : {});
          row = d?.secret;
        } else {
          const d = await createSecretsRegistrySecret({ toolName: spec.id, secretName: label.trim(), ...body,
            scope: registryScope });
          row = d?.secret;
        }
      }
      if (!row?.secretId) throw new Error('Unexpected response from the registry');
      setSaved(row);
      setSecrets({});                       // never keep a secret in memory longer than the save
      const t = await testSecretsRegistrySecret(row.secretId, {});
      setResult(t);
      setStep(1);
    } catch (e) { setError(e?.message || "Couldn't save the connection"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true"
      data-testid="sec-wizard" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}>
      <div className="w-full max-w-xl rounded-lg bg-white shadow-xl">
        <div className="flex items-start gap-2 border-b border-gray-200 px-6 py-4">
          <KeyRound className="mt-0.5 h-5 w-5 text-gray-500" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-900">{editing ? 'Edit' : 'Connect'} {w.title || spec.label || spec.id}</h2>
            {w.intro ? <p className="mt-1 text-xs text-gray-600">{w.intro}</p> : null}
          </div>
          <button type="button" onClick={onCancel} aria-label="Close"
            className="px-2.5 py-1 text-xs font-medium text-gray-500 rounded-md hover:bg-gray-100">×</button>
        </div>
        <ol className="flex gap-1 px-6 pt-3" aria-label="Steps">
          {STEPS.map((s, i) => (
            <li key={s} data-testid={`sec-step-${i}`} aria-current={i === step ? 'step' : undefined}
              className={`flex-1 rounded-md px-2 py-1 text-center text-[11px] font-medium ${i === step ? 'bg-indigo-600 text-white'
                : i < step ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-50 text-gray-400'}`}>{i + 1}. {s}</li>
          ))}
        </ol>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto px-6 py-4 text-sm">
          {step === 0 ? (
            <div className="space-y-3" data-testid="sec-details">
              <label className="block text-xs font-medium text-gray-700">Connection name
                <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="sec-label"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" placeholder={w.namePlaceholder || ''} />
              </label>
              {fields.map((f) => (
                <label key={f.id} className="block text-xs font-medium text-gray-700">{f.label || f.id}
                  <input value={values[f.id] || ''} onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                    data-testid={`sec-field-${f.id}`} placeholder={f.placeholder || ''}
                    className={`mt-1 block rounded-md border border-gray-300 px-3 py-1.5 ${f.short ? 'w-40 text-sm' : 'w-full'} ${f.mono ? 'font-mono text-xs' : 'text-sm'}`} />
                  {values[f.id] && !fieldOk(f, values[f.id]) ? <span className="text-[11px] text-rose-600">{f.invalid || "That doesn't look right."}</span> : null}
                </label>
              ))}
              {secretFields.map((f) => (
                <label key={f.id} className="block text-xs font-medium text-gray-700">{f.label || f.id}
                  <input type="password" autoComplete="new-password" value={secrets[f.id] || ''}
                    onChange={(e) => setSecrets((s) => ({ ...s, [f.id]: e.target.value }))}
                    data-testid={`sec-secret-${f.id}`} placeholder={editing ? 'Leave blank to keep the stored value' : (f.placeholder || '')}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-xs" />
                </label>
              ))}
              <p className="text-[11px] text-gray-500">Secrets are kept in the platform's secrets store — never shown again.</p>
            </div>
          ) : null}
          {step === 1 && result ? (
            <div data-testid="sec-test-result" className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ${result.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
              {result.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4" /> : <XCircle className="mt-0.5 h-4 w-4" />}
              <span>{result.message}</span>
            </div>
          ) : null}
          {error ? <div className="text-xs text-rose-600" data-testid="sec-error">{error}</div> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-3">
          {step === 0 ? <button type="button" onClick={saveAndTest} disabled={!label.trim() || !allOk || busy} data-testid="sec-save-test"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save &amp; test</button> : null}
          {step === 1 ? (result?.ok
            ? <button type="button" data-testid="sec-done" onClick={() => onRegistryCreated?.(saved)}
              className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Done</button>
            : <>
              <button type="button" onClick={() => { setSaved(null); setStep(0); }} data-testid="sec-back"
                className="px-2.5 py-1 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100">Back</button>
              <button type="button" onClick={saveAndTest} disabled={busy}
                className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Test again</button>
            </>) : null}
        </div>
      </div>
    </div>
  );
}
